import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Compass, ClipboardCheck, Landmark, AtSign, CalendarCheck } from 'lucide-react';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const ICONS = { business_nudge: Compass, project_complete_request: ClipboardCheck, bank_details_changed: Landmark, mention: AtSign, review_reminder: CalendarCheck };

function linkFor(n) {
  if (n.link) return n.link;
  const m = /project ([0-9a-f-]{36})/i.exec(n.body || '');
  return m ? `/projects/${m[1]}` : null;
}

export function NotificationsBell() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const refresh = useCallback(() => api.get('/admin/notifications/unread-count').then((r) => setCount(r.data.count || 0)).catch(() => {}), [api]);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); window.addEventListener('admin-notifications:refresh', refresh); return () => { clearInterval(t); window.removeEventListener('admin-notifications:refresh', refresh); }; }, [refresh]);
  useEffect(() => {
    if (!open) return undefined;
    api.get('/admin/notifications').then((r) => setItems((r.data || []).slice(0, 25))).catch(() => {});
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, api]);

  const act = async (n) => {
    if (!n.read_at) { try { await api.post(`/admin/notifications/${n.id}/read`); } catch { /* already read */ } setItems((l) => l.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x))); refresh(); }
    const to = linkFor(n);
    if (to) { setOpen(false); navigate(to); }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="relative p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5 transition-colors" title="Notifications" data-testid="notifications-bell">
        <Bell className="w-5 h-5" />
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F5A623] text-[#1E2530] text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-y-auto rounded-lg border border-white/10 bg-[#2A3240] shadow-2xl z-50 animate-fade-in" data-testid="notifications-panel">
          <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-400 border-b border-white/10">Notifications</div>
          {items.length === 0 && <p className="px-4 py-8 text-sm text-gray-500 text-center">Nothing yet.</p>}
          {items.map((n) => {
            const Icon = ICONS[n.kind] || Bell;
            return (
              <button key={n.id} type="button" onClick={() => act(n)} className={`w-full text-left px-4 py-3 flex gap-3 border-b border-white/5 hover:bg-white/5 transition-colors ${n.read_at ? 'opacity-60' : ''}`} data-testid={`notification-${n.id}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.read_at ? 'bg-white/5 text-gray-400' : 'bg-[#F5A623]/15 text-[#F5A623]'}`}><Icon className="w-4 h-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-gray-100 leading-snug">{n.title}</span>
                  {n.body && <span className="block text-xs text-gray-400 mt-0.5 line-clamp-2">{n.body.replace(/ · project [0-9a-f-]{36}/i, '')}</span>}
                  <span className="block text-[10px] text-gray-500 mt-1">{n.created_at ? formatDistanceToNowStrict(parseISO(n.created_at), { addSuffix: true }) : ''}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
